import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  RadioGroup,
  Typography,
} from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useReactToPrint } from "react-to-print";
import OrderPrintVersion from "../../order/component/OrderPrintVersion";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import moment from "moment";
import bkstApi from "../../api";
import { getCustomerName } from "../../common/util";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import { UserContext } from "../../context/UserContext";
import RadioOptionCard from "../../common/component/RadioOptionCard";
import { useNavigate } from "react-router-dom";
import { bkeStOrderPrintUrl } from "../../common/bkeStRedirect";
const BRAND = "#78374a";
const PRODUCTION_FIREBASE_PROJECT_ID = "bakestreet-f55a0";
const PRINT_PREVIEW_BAKER_ID = "Z3bGmikh3cWqw3HYKGIA";

function PrintSection({ icon, title, subtitle, children, sx }) {
  return (
    <Box
      sx={{
        mb: 3,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: "8px",
            backgroundColor: "rgba(120, 55, 74, 0.1)",
            color: BRAND,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5, ml: "44px" }}>
        {subtitle}
      </Typography>
      {children}
    </Box>
  );
}

export default function OrderPrint(props) {
  const printRef = useRef();
  const { order } = props;
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [type, setType] = useState("");

  const [printActivity, setPrintActivity] = useState(order?.activity?.print || []);

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("preview");

  const hasReceiptPrinter = Boolean(user.config?.receiptPrinterId);
  const isOfficeLayout = type === "compact" || type === "expanded";
  const showPrintPreview =
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID !== PRODUCTION_FIREBASE_PROJECT_ID ||
    user.acctId === PRINT_PREVIEW_BAKER_ID;

  const print = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${getCustomerName(order.customer)} ${moment(order.fulfillment.date).format("MMM DD, YYYY")}`,
    onAfterPrint: () => setStatus("sent"),
  });

  const printPackingSlip = () => {
    bkstApi
      .post(`/my/printer/${user.config.receiptPrinterId}/packing-slip?orderId=${order.id}`)
      .then(() => {
        setOpen(false);
      });
  };

  const handlePrint = () => {
    if (type === "packing-slip") {
      printPackingSlip();
      return;
    }

    print();
  };

  const markPrintSuccessful = () => {
    bkstApi.put(`/my/order/${order.id}/activity/print`, { ts: Date.now() }).then((res) => {
      setPrintActivity([...printActivity, { ts: Date.now() }]);
      setOpen(false);
    });
  };

  useEffect(() => {
    if (!open) {
      setStatus("preview");
      setType("");
    }
  }, [open]);

  const lastPrintTs =
    (printActivity && printActivity.length > 0 && printActivity[printActivity.length - 1].ts) || "";

  const lastUpdTs = order.updTs || order.ts._seconds * 1000;

  let dialogWidth = "sm";
  let title = "Print Order";
  let content = (
    <Box>
      {lastPrintTs && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This order was last updated on {moment(lastUpdTs).format("llll")} (
          {moment(lastUpdTs).fromNow()}) and was last printed on{" "}
          {moment(lastPrintTs).format("llll")} ({moment(lastPrintTs).fromNow()})
        </Alert>
      )}
      {order.items && (
        <RadioGroup value={type} onChange={(e) => setType(e.target.value)}>
          <PrintSection
            icon={<PrintOutlinedIcon fontSize="small" />}
            title="Office Printer"
            subtitle="Letter-size layout for your regular printer."
          >
            <RadioOptionCard
              value="compact"
              title="Compact"
              description="Prints multiple items on one page. Ideal for standard orders."
              selected={type === "compact"}
            />
            <RadioOptionCard
              value="expanded"
              title="Expanded"
              description="Prints each item on a separate page. Ideal for custom orders."
              selected={type === "expanded"}
            />
          </PrintSection>

          <Divider sx={{ mb: 3 }} />

          <PrintSection
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            title="Receipt Printer"
            subtitle="Thermal prints sent to your configured receipt printer."
            sx={{ mb: 0 }}
          >
            <RadioOptionCard
              value="packing-slip"
              title="Packing Slip"
              description={
                hasReceiptPrinter
                  ? "Prints a packing slip on your receipt printer."
                  : "Requires a receipt printer to be configured."
              }
              selected={type === "packing-slip"}
              disabled={!hasReceiptPrinter}
            />
          </PrintSection>
        </RadioGroup>
      )}
      <Box style={{ display: "none" }}>
        <Box ref={printRef}>
          {order && type !== "packing-slip" && <OrderPrintVersion type={type} order={order} />}
        </Box>
      </Box>
    </Box>
  );

  if (status === "sent") {
    dialogWidth = "sm";
    title = "Confirmation";
    content = (
      <Box my={4} align="center">
        <Typography variant="h5" gutterBottom>
          Was the print successful?
        </Typography>
        <Typography variant="body1" color="textSecondary">
          We'll help you keep track of orders you've printed & alert you about upcoming orders you
          haven't yet printed
        </Typography>
      </Box>
    );
  }

  let badgeContent = 0;
  if (lastPrintTs) {
    //stale print
    if (order.updTs && order.updTs > lastPrintTs) {
      badgeContent = <WarningIcon sx={{ fontSize: "16px", color: "#e67e22" }} />;
    } else {
      // up-to-date print
      badgeContent = <CheckOutlinedIcon sx={{ fontSize: "16px", color: "#888" }} />;
    }
  }

  return (
    <>
      <IconButton size="small" onClick={() => setOpen(true)}>
        <Badge
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          badgeContent={badgeContent}
        >
          <PrintOutlinedIcon />
        </Badge>
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth={true} maxWidth={dialogWidth}>
        <DialogTitle sx={{ pr: 6 }}>
          {title}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>{content}</DialogContent>
        {status === "preview" && (
          <DialogActions sx={{ gap: 0.5, px: 2, pb: 2, alignItems: "stretch" }}>
            {showPrintPreview && isOfficeLayout && (
              <>
                <IconButton
                  component="a"
                  href={bkeStOrderPrintUrl(order.id, type)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open in browser"
                  size="large"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                >
                  <OpenInNewOutlinedIcon />
                </IconButton>
                <Button
                  color="secondary"
                  variant="outlined"
                  size="large"
                  sx={{ flex: 1 }}
                  onClick={() => {
                    setOpen(false);
                    navigate(`/print/order/${order.id}?layout=${type}`);
                  }}
                >
                  Preview
                </Button>
              </>
            )}
            <Button
              color="primary"
              variant="contained"
              onClick={handlePrint}
              size="large"
              disabled={!type}
              sx={{ flex: showPrintPreview && isOfficeLayout ? 1 : undefined }}
              fullWidth={!(showPrintPreview && isOfficeLayout)}
            >
              Print
            </Button>
          </DialogActions>
        )}
        {status === "sent" && (
          <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setStatus("preview");
              }}
              fullWidth
            >
              RE-PRINT
            </Button>
            <Button color="primary" variant="contained" onClick={markPrintSuccessful} fullWidth>
              LOOKS GOOD!
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
